<template>
	<div class="scan-keycard" id="scanner">

		<!-- Heading -->
        <h5 class="heading">{{ $t('skipass.scanningHeading') }}</h5>

		<!-- Info message -->
        <AlertMessage class="info" :show="true" :blue="true" type="info" :closable="true">
			<h5>{{ $t('skipass.scanningInfoHeading') }}</h5>
            <p v-html="$t('skipass.scanningInfoBody')"></p>
        </AlertMessage>

		<!-- Warning message (camera loading/denied) -->
		<AlertMessage v-if="!cameraIsOn" class="warning" :show="true" :blue="true" type="warning">
			<h5>{{ $t('skipass.scanningWarningHeading') }}</h5>
            <p v-html="$t('skipass.scanningWarningBody')"></p>
        </AlertMessage>

		<!-- Scanner -->
		<div :class="this.scannerStatus" class="camera" id="camera">
			
			<!-- Left/right blur -->
			<span class="camera-left"></span>
			<span class="camera-right"></span>

			<!-- Found number alert -->
			<div v-if="showFoundNumber" class="found-number">
				<svg data-name="Lager 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" v-bind:svg-inline="''" v-bind:class="'icon'" v-bind:focusable="'false'" v-bind:role="'presentation'" v-bind:tabindex="'-1'"><path class="cls-1" d="M5.43 11a1 1 0 01-.76-.35l-3.43-4a1 1 0 011.52-1.3l2.5 2.92L9.13 1.5a1 1 0 111.74 1l-4.57 8a1 1 0 01-.78.5h-.09"/></svg>
				<span>{{ keycardNumber }}</span>
			</div>

			<!-- Rectangle overlay -->
			<div class="rectangle"></div>

			<!-- Video stream -->
			<video 
				id="video" 
				:width="this.width"
				:srcObject.prop="videoSrcObject"
				autoplay 
				playsinline
				@playing="videoCanPlayHandler()"
			>
				Video stream not available.
			</video>

		</div>

		<!-- Canvas for OCR processing -->
		<canvas id="canvas" :width="this.width" :height="this.height"> </canvas>

	</div>
</template>

<script>
import AlertMessage from '../atoms/AlertMessage'
import '../../utilities/tesseract.min.js'
var Tesseract = window.Tesseract;

export default {
	name: 'ScanKeycard',

	components: {
        AlertMessage
	},

	data() {
		return {
            keycardNumber: null,
			width: 270,
			height: 0,
			streaming: false,
			video: null,
			videoSrcObject: null,
			canvas: null,
			photo: null,
			startButton: null,
			canvasContext: null,
			canvasDataUrl: null,
			timerId: null,
			scannerStatus: 'scanner-off',
			cameraIsOn: false,
			showFoundNumber: false
		}
	},

	methods: {
		startScanner() {
			location.href = "#";
			location.href = "#scanner";

			this.scannerStatus = 'scanner-on';

			// Reset streaming flag to force recalculation of dimensions
			this.streaming = false;
    
			// Initialize camera with specific constraints
			this.initCamera();

			// Start taking pictures every second
			this.timerId = setInterval(this.takePicture, 1000);

			//Stop scanner after 10 seconds
			setTimeout(this.stopScanner, 30000);
		},

		stopScanner() {
			this.keycardNumber = '';
			this.scannerStatus = 'scanner-off';
			clearInterval(this.timerId);

			// Stop all camera tracks to fully release the camera
			if (this.videoSrcObject) {
				const tracks = this.videoSrcObject.getTracks();
				tracks.forEach(track => track.stop());
				this.videoSrcObject = null;
			}
			
			// Reset dimensions and streaming flag
			this.width = 270;
			this.height = 0;
			this.streaming = false;

			const canvas = document.getElementById("canvas");

			canvas.width = this.width;
			canvas.height = this.height;

			//Send keycard number to parent component
			this.$emit('keycard-scanned', this.keycardNumber);
		},

		// Initialize the camera with consistent constraints
		initCamera() {
			if (this.videoSrcObject) {
				const tracks = this.videoSrcObject.getTracks();
				tracks.forEach(track => track.stop());
				this.videoSrcObject = null;
			}
			
			// Request specific dimensions to help maintain consistency
			navigator.mediaDevices
			.getUserMedia({ 
				video: { 
				facingMode: "environment",
				width: { ideal: 1280 },
				height: { ideal: 720 }
				}, 
				audio: false 
			})
			.then((stream) => {
				this.videoSrcObject = stream;
				this.cameraIsOn = true;
			})
			.catch((error) => {
				// eslint-disable-next-line no-console
				console.error('Error accessing camera:', error);
			});
		},

		videoCanPlayHandler() {
			const video = document.getElementById("video");

			// Determine orientation and normalize it
			const isLandscape = video.videoWidth > video.videoHeight;
			
			// Use consistent orientation calculation
			if (isLandscape) {
				this.height = video.videoHeight / (video.videoWidth / this.width);
			} else {
				this.width = 270; // Keep width fixed
				this.height = (video.videoHeight / video.videoWidth) * this.width;

				// Crop wrapper height for design purpose
				const camera = document.getElementById("camera");
				camera.style.height = this.width + 'px';
				camera.style.overflow = 'hidden';
			}
			
			if (isNaN(this.height)) {
				this.height = this.width / (4 / 3);
			}
			
			// Update canvas to match video dimensions
			const canvas = document.getElementById("canvas");
			canvas.width = this.width;
			canvas.height = this.height;
			
			this.streaming = true;
		},

		clearPhoto() {
			const canvas = document.getElementById("canvas");
			this.canvasContext.fillStyle = "#AAA";
			this.canvasContext.fillRect(0, 0, canvas.width, canvas.height);
			this.canvasDataUrl = canvas.toDataURL("image/png");
		},

		takePicture() {
			const video = document.getElementById("video");
			const canvas = document.getElementById("canvas");
			
			if (this.width && this.height && video.videoWidth && video.videoHeight) {
				// Make sure canvas has valid dimensions
				canvas.width = this.width;
				canvas.height = this.height;
				
				// Clear the canvas first
				this.canvasContext.clearRect(0, 0, canvas.width, canvas.height);
				
				try {
					// Draw the current video frame to the canvas
					this.canvasContext.drawImage(video, 0, 0, this.width, this.height);
					
					// Convert canvas to data URL properly
					this.canvasDataUrl = canvas.toDataURL("image/png");
					
					// Process with Tesseract
					(async () => {
						try {
							// Create a proper blob from canvas data
							const dataURL = canvas.toDataURL('image/png');

							// Validate dataURL format
							if (!dataURL || !dataURL.startsWith('data:image/png')) {
								// eslint-disable-next-line no-console
								console.error('Invalid data URL format');
								return;
							}
							
							// Convert data URL to blob
							const blobData = this.dataURItoBlob(dataURL);
							
							// Calculate rectangle proportionally
							// Make sure rectangle coordinates are relative to current canvas dimensions
							const rectangle = this.calculateScanRectangle();
							
							// Create and initialize worker
							const worker = await Tesseract.createWorker('eng');
							await worker.setParameters({
								tessedit_char_whitelist: '0123456789',
							});
							
							// Recognize text from blob
							const { data: { text } } = await worker.recognize(blobData, { rectangle });
							
							const newString = text.replace(/\r?\n|\r/g, ''); // Using global flag
							if (newString.length === 23) {
								this.keycardNumber = newString;
								this.showFoundNumber = true;

								setTimeout(() => {
									this.scannerStatus = 'scanner-off';
									clearInterval(this.timerId);
									
									// Send keycard number to parent component
									this.$emit('keycard-scanned', this.keycardNumber);
								}, 1000);
							}
							
							await worker.terminate();
						} catch (err) {
							// eslint-disable-next-line no-console
							console.error('OCR error:', err);
						}
					})();
				} catch (err) {
					// eslint-disable-next-line no-console
					console.error('Canvas draw error:', err);
				}
			} else {
				// eslint-disable-next-line no-console
				console.warn('Invalid dimensions, not taking picture', {
					width: this.width,
					height: this.height,
					videoWidth: video.videoWidth,
					videoHeight: video.videoHeight
				});
				this.clearPhoto();
			}
		},

		// Helper method to convert dataURI to Blob
		dataURItoBlob(dataURI) {
			// Convert base64 to raw binary data held in a string
			const byteString = atob(dataURI.split(',')[1]);
			
			// Get content type from data URI
			const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
			
			// Write the bytes of the string to an ArrayBuffer
			const ab = new ArrayBuffer(byteString.length);
			const ia = new Uint8Array(ab);
			
			for (let i = 0; i < byteString.length; i++) {
				ia[i] = byteString.charCodeAt(i);
			}
			
			// Create a blob with type
			return new Blob([ab], { type: mimeString });
		},

		// Calculate scan rectangle based on current dimensions
		calculateScanRectangle() {
			// Original rectangle was { left: 35, top: 60, width: 200, height: 70 }
			// We need to scale it based on the current canvas dimensions
			const originalWidth = 270; // Your original reference width
			
			// Calculate scale factor
			const scaleFactor = this.width / originalWidth;
			
			return {
				left: Math.round(12 * scaleFactor),
				top: Math.round(30 * scaleFactor),
				width: Math.round(246 * scaleFactor),
				height: Math.round(41 * scaleFactor)
			};
		}
	},

	computed: {
		
	},
	created() {
		
    },
	mounted() {
		//Get canvas context
		var c = document.getElementById("canvas");
		var ctx = c.getContext("2d");    
		this.canvasContext = ctx;

		// Listen for orientation changes
		window.addEventListener('orientationchange', () => {
			// eslint-disable-next-line no-console
			console.log('Orientation changed');

			// Reset streaming flag to force recalculation
			this.streaming = false;

			// Wait for orientation change to complete
			setTimeout(this.videoCanPlayHandler, 300);
		});

		this.clearPhoto();
        this.startScanner();
	},
    beforeDestroy() {
        clearInterval(this.timerId);

		// Clean up event listener
		window.removeEventListener('orientationchange', this.orientationChangeHandler);
  
		// Ensure camera is fully released
		if (this.videoSrcObject) {
			const tracks = this.videoSrcObject.getTracks();
			tracks.forEach(track => track.stop());
		}
    }
	
}
</script>

<style lang="scss">
.scan-keycard {
    box-sizing: border-box;
    width: 100%;
	margin: 10px 0 0 0;

    .heading {
		font-size: 28px;
		line-height: 38px;
		text-align: center;
        margin-bottom: 16px;
    }

    .info,
	.warning {
        margin-bottom: 16px;
    }

	.camera {
		position: relative;
		margin: 0 auto;
		display: block;

		&.scanner-off {
			display: none;
		}
	}

	.found-number {
		position: absolute;
		top: 85px;
		left: 30px;
		width: 210px;
		background: black;
		font-size: 12px;
		color: #fff;
		text-transform: uppercase;
		padding: 2px 10px;
		text-align: center;
		z-index: 2;
		box-sizing: border-box;
		border-radius: 5px;

		span {
			vertical-align: middle;
		}

		svg {
			display: inline-block;
			width: 10px;
			height: 10px;
			fill: #7FC3BB;
			margin-right: 6px;
			vertical-align: middle;
		}
	}

	.rectangle {
		position: absolute;
		width: 246px;
		height: 41px;
		z-index: 1;
		top: 30px;
		left: 12px;

		-webkit-mask: conic-gradient(at 15px 15px, transparent 75%, #000 0) 0 0 / calc(100% - 15px) calc(100% - 15px), linear-gradient(#000 0 0) content-box;
		outline: 3px solid #04befb;
		outline-offset: -5px;
		padding: 8px;
		box-sizing: border-box;
	}
  
	#canvas {
		display: none;
	}
  
	.camera {
		width: 270px;
		display: block;

		video {
			display: block;
		}
		
		&:before {
			top: 0;
			height: 32px;
		}

		&:after {
			bottom: 0;
			height: calc(100% - 69px);
		}

		&:after, 
		&:before {
			-webkit-backdrop-filter: blur(5px);
			backdrop-filter: blur(5px);
			content: "";
			display: block;
			background: rgba(0, 0, 0, 0.25);
			left: 0;
			overflow: hidden;
			position: absolute;
			width: 100%;
			z-index: 1;
		}

		&-left {
			left: 0;
		}

		&-right {
			right: 0;
		}

		&-left, 
		&-right {
			display: block;
			width: 14px;
			height: 41px;
			top: 30px;
			-webkit-backdrop-filter: blur(5px);
			backdrop-filter: blur(5px);
			background: rgba(0, 0, 0, 0.25);
			overflow: hidden;
			position: absolute;
		}
	}
}
</style>
